import React, { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { Broadcast, Users, ChatCircleDots, FilmReel, Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { fetchActiveLive, fetchLiveStreams, fetchAgoraToken, startLiveStream, stopLiveStream } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLACEHOLDER = "https://images.unsplash.com/photo-1511406361295-0a1ff814c0ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBkYXJrJTIwYXJjaGl0ZWN0dXJlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc4NzQ3MjAxfDA&ixlib=rb-4.1.0&q=85";

export default function Live() {
  const [active, setActive] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | joining | joined | error | unavailable
  const [statusMsg, setStatusMsg] = useState("");
  const [remoteUsers, setRemoteUsers] = useState([]);
  const clientRef = useRef(null);
  const videoContainerRef = useRef(null);
  const { user, isHost } = useAuth();

  useEffect(() => {
    fetchActiveLive().then(setActive).catch(() => {});
    fetchLiveStreams().then(setSchedule).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      // cleanup on unmount
      try {
        if (clientRef.current) {
          clientRef.current.leave();
          clientRef.current.removeAllListeners();
          clientRef.current = null;
        }
      } catch {}
    };
  }, []);

  const join = async () => {
    if (!active?.is_live || !active.channel_name) return;
    setStatus("joining");
    setStatusMsg("Requesting broadcast token…");
    try {
      const t = await fetchAgoraToken(active.channel_name, 0, 2);
      if (t.mock) {
        setStatus("unavailable");
        setStatusMsg("Agora keys are not configured yet — this is a preview of the live room. Once App ID & Certificate are provided, viewers will see the live video here.");
        return;
      }
      const client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
      client.setClientRole("audience");
      clientRef.current = client;
      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "video" && videoContainerRef.current) {
          user.videoTrack.play(videoContainerRef.current);
        }
        if (mediaType === "audio") {
          user.audioTrack.play();
        }
        setRemoteUsers((u) => [...u.filter((x) => x.uid !== user.uid), user]);
      });
      client.on("user-unpublished", (user) => {
        setRemoteUsers((u) => u.filter((x) => x.uid !== user.uid));
      });
      await client.join(t.app_id, active.channel_name, t.token, t.uid || null);
      setStatus("joined");
      setStatusMsg("Connected");
    } catch (err) {
      setStatus("error");
      setStatusMsg(err?.message || "Failed to join channel");
    }
  };

  return (
    <div data-testid="live-page">
      {/* Header */}
      <section className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
          <div className="overline text-[#FF3B30] mb-3 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full bg-[#FF3B30] ${active?.is_live ? "animate-pulseLive" : "opacity-50"}`} />
            {active?.is_live ? "On Air" : "Broadcast Room"}
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl text-ivory leading-none">
            {active?.title || "Live Broadcast Room"}
          </h1>
          {active?.description && <p className="text-[#A1A1AA] mt-4 max-w-3xl">{active.description}</p>}
        </div>
      </section>

      {/* Player + sidebar */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="aspect-video bg-black border border-[#2A2A2D] relative overflow-hidden">
            {status !== "joined" && (
              <>
                <img src={active?.thumbnail || PLACEHOLDER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/40" />
              </>
            )}
            <div ref={videoContainerRef} className="absolute inset-0" />
            <div className="absolute inset-0 flex items-center justify-center">
              {status === "idle" && (
                <div className="text-center px-6">
                  {active?.is_live ? (
                    <>
                      <div className="overline text-[#FF3B30] mb-3 flex items-center justify-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulseLive" /> LIVE NOW
                      </div>
                      <h3 className="font-serif text-3xl text-ivory mb-6">{active.title}</h3>
                      <button
                        onClick={join}
                        data-testid="join-live-button"
                        className="inline-flex items-center gap-3 h-12 px-8 bg-[#8A2E2E] hover:bg-[#A33B3B] transition-colors text-sm tracking-wider"
                      >
                        <Broadcast size={18} weight="duotone" /> Join Broadcast
                      </button>
                    </>
                  ) : (
                    <div>
                      <div className="overline text-[#B79C7B] mb-3">Off Air</div>
                      <h3 className="font-serif text-3xl text-ivory">No live broadcast at the moment</h3>
                      <p className="text-[#A1A1AA] mt-3 max-w-md">Check the schedule on the right for upcoming sessions.</p>
                    </div>
                  )}
                </div>
              )}
              {status === "joining" && <p className="text-ivory">{statusMsg}</p>}
              {status === "unavailable" && (
                <div className="text-center px-8 max-w-xl">
                  <div className="overline text-[#B79C7B] mb-3">Preview Mode</div>
                  <p className="text-[#F4F0EA] leading-relaxed">{statusMsg}</p>
                </div>
              )}
              {status === "error" && <p className="text-[#FF3B30]">{statusMsg}</p>}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6 text-[#A1A1AA]">
              <span className="flex items-center gap-2"><Users size={16} weight="duotone" /> {active?.viewer_count || 0} viewers</span>
              <span className="flex items-center gap-2"><Broadcast size={16} weight="duotone" /> Channel: {active?.channel_name || "—"}</span>
            </div>
            {status === "joined" && (
              <span className="overline text-[#FF3B30] flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulseLive" /> LIVE
              </span>
            )}
          </div>
        </div>

        {/* Side: schedule */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="border border-[#2A2A2D] bg-[#121214]">
            <div className="px-5 py-4 border-b border-[#2A2A2D] overline text-[#B79C7B] flex items-center gap-2">
              <FilmReel size={14} weight="duotone" /> Schedule
            </div>
            <div className="divide-y divide-[#2A2A2D] max-h-[500px] overflow-y-auto scrollbar-thin">
              {schedule.length === 0 && <div className="p-5 text-sm text-[#71717A]">No scheduled streams.</div>}
              {schedule.map((s) => (
                <div key={s.id} className="p-5" data-testid={`schedule-item-${s.id}`}>
                  <div className="flex items-center gap-2 overline text-[#71717A]">
                    {s.is_live ? (
                      <span className="text-[#FF3B30] flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulseLive" /> LIVE
                      </span>
                    ) : (
                      <span>Scheduled</span>
                    )}
                  </div>
                  <div className="font-serif text-lg text-ivory mt-1 leading-tight">{s.title}</div>
                  <div className="text-xs text-[#A1A1AA] mt-1">{s.host_name || "—"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Host controls */}
          {isHost && schedule.length > 0 && (
            <div className="border border-[#2A2A2D] bg-[#121214] p-5">
              <div className="overline text-[#B79C7B] mb-3 flex items-center gap-2">
                <Broadcast size={14} weight="duotone" /> Host Controls
              </div>
              <div className="space-y-2">
                {schedule.filter((s) => !s.is_live).slice(0, 3).map((s) => (
                  <button
                    key={s.id}
                    onClick={async () => { await startLiveStream(s.id); fetchActiveLive().then(setActive); fetchLiveStreams().then(setSchedule); }}
                    className="w-full text-left px-4 py-2 border border-[#2A2A2D] hover:border-[#8A2E2E] text-sm text-[#A1A1AA] hover:text-ivory transition-colors"
                  >
                    ▶ Start: {s.title}
                  </button>
                ))}
                {active?.is_live && (
                  <button
                    onClick={async () => { await stopLiveStream(active.id); fetchActiveLive().then(setActive); fetchLiveStreams().then(setSchedule); }}
                    className="w-full text-left px-4 py-2 border border-[#8A2E2E] hover:bg-[#8A2E2E] text-sm text-[#FF3B30] hover:text-ivory transition-colors"
                  >
                    ■ Stream Band Karo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Login prompt for non-logged-in users */}
          {!user && (
            <div className="border border-[#2A2A2D] bg-[#121214] p-5">
              <div className="overline text-[#B79C7B] mb-3 flex items-center gap-2">
                <Lock size={14} weight="duotone" /> Host Access
              </div>
              <p className="text-sm text-[#A1A1AA] mb-3">Live stream start karne ke liye host account chahiye.</p>
              <Link to="/login" className="text-sm text-[#B79C7B] hover:underline">Login karein →</Link>
            </div>
          )}

          <div className="border border-[#2A2A2D] bg-[#121214] p-5">
            <div className="overline text-[#B79C7B] mb-3 flex items-center gap-2"><ChatCircleDots size={14} weight="duotone" /> Community</div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed">
              The live chat will be enabled once an active broadcast is on air. Until then, browse the archive of past majalis from the categories page.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
